import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { usePostMasterUpdate } from '../../../../api/masters/masters';
import { ErrorInfo, MasterDetail } from '../../../../api/model';
import { useDefaultOptions } from '../../../../hooks/api';
import ConfirmModal from '../../../modules/ConfirmModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import { MasterUpload } from './MasterUpload';
import { MasterDownload } from './MasterDownload';

interface Props {
  master: MasterDetail;
}
function MasterForm({ master }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [isConfirm, setIsConfirm] = useState(false);
  const navigate = useNavigate();
  const options = useDefaultOptions(language);
  const [btnEnabled, setBtnEnabled] = useState(true);

  const { control, register, handleSubmit, setValue, getValues, reset } = useForm<MasterDetail>({
    defaultValues: {
      master_version_id: master.master_version_id,
      master_name: master.master_name,
      memo: master.memo,
      template_items: master.template_items,
      version_number: master.version_number,
    },
  });
  const { fields, append, remove } = useFieldArray({ name: `template_items`, control });

  const { isLoading, mutateAsync, isSuccess } = usePostMasterUpdate<AxiosError<ErrorInfo>, unknown>(options);

  const [image, setImage] = useState<string | null>(null);
  const setImagePath = (imagePath: string) => {
    setImage(imagePath);
    setValue('image_temp_path', imagePath);
  };

  const setExcelPath = (excelPath: string) => {
    setValue('excel_temp_path', excelPath);
  };

  const onSubmit = useCallback(
    () =>
      handleSubmit((data: MasterDetail) => {
        const postMasterSave = async () => {
          await mutateAsync({
            masterVersionId: Number(data.master_version_id),
            data,
          });
        };

        void postMasterSave();
      })(),
    [mutateAsync, handleSubmit]
  );

  useEffect(() => {
    if (isSuccess) {
      navigate('..');
    }
  }, [navigate, isSuccess]);

  return (
    <section>
      <div className="mastr-head2">
        <h3>
          <span>{t('マスタ情報')}</span>
        </h3>
        <div className="master-right-navi">
          <ul>
            <MasterDownload masterVersionId={Number(master.master_version_id)} />
          </ul>
        </div>
      </div>

      <form>
        <div className="wfr">
          <div className="grid3 labip4 mb20">
            <div>
              <label htmlFor="my3-1">{t('マスタ名')}</label>
              <input type="text" {...register('master_name')} readOnly />
            </div>
            <div>
              <label htmlFor="version">{t('バージョン')}</label>
              <input type="number" {...register('version_number')} readOnly />
            </div>
          </div>
          <div className="fileupfr mb30">
            <span>{t('添付ファイル')}</span>
            {master.master_version_id && (
              <MasterUpload
                masterVersionId={master.master_version_id}
                isMaster={Number(master.is_master)}
                setImagePath={setImagePath}
                setExcelPath={setExcelPath}
                setBtnEnabled={setBtnEnabled}
              />
            )}
          </div>

          {master.is_master === 1 && (
            <>
              <div className="master-navi">
                <ul>
                  <li className="" onClick={() => append({ item_name: '', value: '' })}>
                    <i className="fa-solid fa-arrow-up-from-bracket" />
                    {t('マスタ項目追加')}
                  </li>
                </ul>
              </div>
              <div className="master-separatefr">
                <div className="mstable">
                  <table className="table39">
                    <tbody>
                      <tr>
                        <th> </th>
                        <th>{t('マスタ項目名')}</th>
                        <th>{t('マスタ設定値')}</th>
                      </tr>
                      {fields.map((item, i) => (
                        <tr key={String(item.template_item_id)}>
                          <td>
                            <span className="ck-box" onClick={() => remove(i)}>
                              <i className="fa-solid fa-circle-xmark" />
                            </span>
                          </td>
                          <td>
                            <input type="text" {...register(`template_items.${i}.item_name`)} />
                          </td>
                          <td>
                            <input type="text" {...register(`template_items.${i}.value`)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="msdata">
                  {/* <img src="./images/202303220909431.PNG" alt="" /> */}
                  {!image && <img src={master.s3_image_url} alt="" />}
                  {image && <img src={getValues('image_temp_path')} alt="" />}
                </div>
              </div>
            </>
          )}

          <div className="formarea1">
            <label htmlFor="memo">{t('メモ')}</label>
            <textarea id="memo" rows={10} cols={33} {...register('memo')} />
          </div>

          <div className="btnfr btnfr2 mb0">
            <div style={{ width: 'auto' }}>
              <span className="btn2 btnbg-g mr10">
                <Link to={`/${language}/masterslist`}>{t('キャンセル')}</Link>
              </span>

              <span className="mr10">
                <input type="button" value="元に戻す" className="btn1 btnbg-g"
                  onClick={() => {
                    setBtnEnabled(true);
                    reset();
                  }
                  } />
              </span>
              <span>
                <input type="button" value="更新" className="btn1 lsp1" onClick={() => setIsConfirm(true)} disabled={!btnEnabled} />
              </span>
            </div>
          </div>
        </div>
      </form>

      {isConfirm && (
        <ConfirmModal onClickOk={() => onSubmit()} onClickCancel={() => setIsConfirm(false)}>
          <div className="poptxt">
            <p>{t('更新します。よろしいですか？')}</p>
          </div>
        </ConfirmModal>
      )}
      {isLoading && <SpinnerModal />}
    </section>
  );
}

export { MasterForm };
